import type { StatusTone } from '@martinus/styles/client/StatusBadge.js';
import type { ContractStatus, SupplementStatus } from '../../../api/getSupplementStatus.js';

export interface StatusPresentation {
  tone: StatusTone;
  label: string;
  /** literal iconify class so the tailwind scanner picks it up */
  iconClass: string;
  description: string;
}

/**
 * How each supplement state is presented. Green is reserved for "in effect", an unsigned draft is a warning
 * because it still needs a signature, and only a termination is an error state. Future and expired supplements
 * are neutral, they are simply not the state the user is currently looking at.
 */
export const SUPPLEMENT_STATUS_PRESENTATION: Record<SupplementStatus, StatusPresentation> = {
  editing: {
    tone: 'info',
    label: 'In Bearbeitung',
    iconClass: 'icon-[material-symbols--edit-outline]',
    description: 'Dieser Nachtrag wird gerade bearbeitet.',
  },
  draft: {
    tone: 'warning',
    label: 'Nicht unterzeichnet',
    iconClass: 'icon-[material-symbols--draft-outline]',
    description: 'Der Nachtrag ist noch nicht unterschrieben und damit nicht in Kraft.',
  },
  active: {
    tone: 'success',
    label: 'Aktiv',
    iconClass: 'icon-[material-symbols--check-circle-outline]',
    description: 'Der Nachtrag ist zum gewählten Datum in Kraft.',
  },
  partial: {
    tone: 'success',
    label: 'Teilweise aktiv',
    iconClass: 'icon-[material-symbols--adjust]',
    description: 'Nur ein Teil der geänderten Bereiche ist zum gewählten Datum in Kraft.',
  },
  future: {
    tone: 'neutral',
    label: 'Noch nicht aktiv',
    iconClass: 'icon-[material-symbols--schedule]',
    description: 'Der Nachtrag tritt erst nach dem gewählten Datum in Kraft.',
  },
  expired: {
    tone: 'neutral',
    label: 'Ausgelaufen',
    iconClass: 'icon-[material-symbols--history]',
    description: 'Der Nachtrag war vor dem gewählten Datum in Kraft.',
  },
  terminated: {
    tone: 'error',
    label: 'Durch Kündigung beendet',
    iconClass: 'icon-[material-symbols--block]',
    description: 'Der Nachtrag wurde durch die Kündigung des Vertrages beendet.',
  },
  inactive: {
    tone: 'neutral',
    label: 'Inaktiv',
    iconClass: 'icon-[material-symbols--remove]',
    description: 'Der Nachtrag hat keine Laufzeit und ist daher nicht in Kraft.',
  },
};

export const CONTRACT_STATUS_PRESENTATION: Record<ContractStatus, StatusPresentation> = {
  active: {
    tone: 'success',
    label: 'Aktiv',
    iconClass: 'icon-[material-symbols--check-circle-outline]',
    description: 'Der Vertrag läuft zum gewählten Datum.',
  },
  unsigned: {
    tone: 'warning',
    label: 'Nicht unterzeichnet',
    iconClass: 'icon-[material-symbols--draft-outline]',
    description: 'Der Vertrag ist noch nicht unterschrieben.',
  },
  future: {
    tone: 'neutral',
    label: 'Beginnt später',
    iconClass: 'icon-[material-symbols--schedule]',
    description: 'Der Vertrag beginnt erst nach dem gewählten Datum.',
  },
  terminated: {
    tone: 'error',
    label: 'Beendet',
    iconClass: 'icon-[material-symbols--block]',
    description: 'Der Vertrag war zum gewählten Datum bereits gekündigt.',
  },
  expired: {
    tone: 'neutral',
    label: 'Nicht aktiv',
    iconClass: 'icon-[material-symbols--remove]',
    description: 'Der Vertrag ist zum gewählten Datum nicht in Kraft.',
  },
};

export const SUPPLEMENT_SECTION_LABEL: Record<'general' | 'ag1' | 'ag2', string> = {
  general: 'Allg.',
  ag1: 'AG1',
  ag2: 'AG2',
};
